import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import { Add } from '@factorialco/factorial-one-react/icons/app'
import { MeetingDetailFragment, MeetingTasksListDocument } from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { timeAgoWhenRecent } from 'modules/one_on_ones/lib/date'

import { NewTaskModal } from './NewTaskModal'
import { SuggestedTasks } from './SuggestedTasks'

interface Props {
  meeting: MeetingDetailFragment
}

export function MeetingTasksList({ meeting }: Props) {
  const { navigation } = useNavigation()
  const [newAddModal, setNewAddModal] = useState(false)

  const source = useGraphqlDataSource({
    document: MeetingTasksListDocument,
    mapData: (data) => data.oneOnOnes.tasksConnection.edges,
    mapPagination: (data) => ({
      total: Number(data.oneOnOnes.tasksConnection.totalCount),
    }),
    mapVariables: () => ({
      meetingId: meeting.id,
    }),
    itemUrl(item) {
      return navigation.tasks.taskId.fullPath({ taskId: String(item.node.taskId) })
    },
    primaryActions: () => ({
      label: i18n.t('one_on_ones.meeting.tasks.button'),
      icon: Add,
      onClick: () => setNewAddModal(true),
    }),
  })

  const hasDraftTasks = (meeting.talkingPoints?.at(0)?.tasksDrafts || []).length > 0

  return (
    <Box width="full" gap="s24">
      {newAddModal && <NewTaskModal onClose={() => setNewAddModal(false)} meeting={meeting} />}

      <Text.Normal size="300" weight="semibold">
        {i18n.t('one_on_ones.meeting.tasks.title')}
      </Text.Normal>

      {hasDraftTasks ? <SuggestedTasks meeting={meeting} /> : null}

      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('one_on_ones.meeting.tasks.table.name'),
                  render: (item) => ({
                    type: 'text',
                    value: item.node.name,
                  }),
                },
                {
                  label: i18n.t('one_on_ones.meeting.tasks.table.assigned_to'),
                  render: (item) => ({
                    type: 'avatarList',
                    value: {
                      avatarList: item.node.assignees.map((assignee) => ({
                        type: 'person',
                        firstName: assignee.firstName,
                        lastName: assignee.lastName,
                        src: assignee.avatar ? buildUrl(assignee.avatar.url) : undefined,
                      })),
                    },
                  }),
                },
                {
                  label: i18n.t('one_on_ones.meeting.tasks.table.due'),
                  render: (item) => ({
                    type: 'text',
                    value: item.node.dueOn ? timeAgoWhenRecent(item.node.dueOn) : '',
                  }),
                },
              ],
            },
          },
        ]}
      />
    </Box>
  )
}
